import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Grid,
  LinearProgress,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { Navigate, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { HttpMainApi } from "../../interface/main-api";
import { userState } from "../../interface/MainInterface";

type MetricType =
  | "LOGIN_SUCCESS"
  | "ROBOT_CLEANER_MENU_CLICK"
  | "SCREEN_GOLF_MENU_CLICK"
  | "SMALL_LIBRARY_MENU_CLICK"
  | "BOOK_LOAN_STATUS_VIEW"
  | "BOOK_CAFE_MENU_CLICK"
  | "BOOK_CAFE_GUIDE_CLICK"
  | "BOOK_CAFE_GUIDE_DETAIL_CLICK"
  | "BOOK_CAFE_BOARD_GAME_LIST_CLICK";

interface PropsType {
  userState: userState;
}

interface MetricDefinition {
  type: MetricType;
  label: string;
  description: string;
}

interface DailyCountRow {
  id: string;
  date: string;
  count: number;
}

interface RawLogRow {
  id: string;
  displayTime: string;
  eventType: string;
  userId: string;
  userName: string;
  path: string;
  source: string;
  raw: any;
}

const mainApi = new HttpMainApi();

const METRICS: MetricDefinition[] = [
  {
    type: "LOGIN_SUCCESS",
    label: "로그인 성공 수",
    description: "기간 내 로그인 성공 이벤트",
  },
  {
    type: "ROBOT_CLEANER_MENU_CLICK",
    label: "청소 로봇 메뉴 클릭수",
    description: "창문 로봇청소기 예약 메뉴 진입",
  },
  {
    type: "SCREEN_GOLF_MENU_CLICK",
    label: "골프예약 메뉴 클릭수",
    description: "스크린 골프 예약 메뉴 진입",
  },
  {
    type: "SMALL_LIBRARY_MENU_CLICK",
    label: "작은도서관 클릭수",
    description: "작은도서관 메뉴/배너 진입",
  },
  {
    type: "BOOK_LOAN_STATUS_VIEW",
    label: "도서 대여 현황 조회수",
    description: "도서 대여 현황 페이지 진입",
  },
  {
    type: "BOOK_CAFE_MENU_CLICK",
    label: "북카페 예약 클릭수",
    description: "북카페 예약 메뉴 및 예약 버튼 진입",
  },
  {
    type: "BOOK_CAFE_GUIDE_CLICK",
    label: "북카페 안내 클릭수",
    description: "북카페 이용 안내 메뉴 진입",
  },
  {
    type: "BOOK_CAFE_GUIDE_DETAIL_CLICK",
    label: "북카페 상세 가이드 클릭수",
    description: "북카페 공간별 상세 가이드 진입",
  },
  {
    type: "BOOK_CAFE_BOARD_GAME_LIST_CLICK",
    label: "보드게임 리스트 클릭수",
    description: "북카페 보드게임 리스트 열기",
  },
];

function formatDateInput(date: Date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function getDefaultDateRange() {
  const today = new Date();
  const start = new Date(today);
  start.setDate(today.getDate() - 6);
  return {
    startDate: formatDateInput(start),
    endDate: formatDateInput(today),
  };
}

function getResultPayload(response: any) {
  return response?.response?.result ?? response?.response ?? response ?? {};
}

function toNumber(value: unknown) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function formatCompactDateTime(value: unknown) {
  if (value === undefined || value === null) return "";
  const text = String(value);
  const compactMatch = text.match(/^(\d{4})(\d{2})(\d{2})(\d{2})?(\d{2})?(\d{2})?/);
  if (compactMatch) {
    const [, year, month, day, hour = "00", minute = "00", second = "00"] = compactMatch;
    return `${year}-${month}-${day} ${hour}:${minute}:${second}`;
  }
  return text;
}

function getDateFromValue(value: unknown) {
  if (value === undefined || value === null) return "";
  const text = String(value);
  const dateMatch = text.match(/(\d{4})[-/]?(\d{2})[-/]?(\d{2})/);
  if (!dateMatch) return text;
  const [, year, month, day] = dateMatch;
  return `${year}-${month}-${day}`;
}

function getLogDate(row: RawLogRow) {
  return getDateFromValue(row.displayTime || row.raw?.createdAt || row.raw?.inputDt || row.raw?.timestamp);
}

function normalizeRawLogs(payload: any): RawLogRow[] {
  const source = Array.isArray(payload?.rawLogs)
    ? payload.rawLogs
    : Array.isArray(payload?.logs)
      ? payload.logs
      : Array.isArray(payload?.items)
        ? payload.items
        : Array.isArray(payload?.events)
          ? payload.events
          : Array.isArray(payload) && payload.some((row: any) => row?.eventType || row?.userId || row?.path)
            ? payload
            : [];

  return source.map((row: any, index: number) => {
      const eventTime = row?.eventTime ?? row?.createdAt ?? row?.inputDt ?? row?.timestamp ?? row?.sk ?? "";
      return {
        id: String(row?.id ?? row?.logId ?? row?.sk ?? `${eventTime}-${index}`),
        displayTime: formatCompactDateTime(eventTime),
        eventType: String(row?.eventType ?? row?.metricType ?? ""),
        userId: String(row?.userId ?? ""),
        userName: String(row?.userName ?? ""),
        path: String(row?.path ?? row?.metadata?.path ?? row?.url ?? ""),
        source: String(row?.source ?? row?.metadata?.source ?? ""),
        raw: row,
      };
  });
}

function normalizeDailyCounts(payload: any, logs: RawLogRow[]): DailyCountRow[] {
  const source = Array.isArray(payload?.dailyCounts)
    ? payload.dailyCounts
    : Array.isArray(payload?.daily)
      ? payload.daily
      : Array.isArray(payload?.counts)
        ? payload.counts
        : Array.isArray(payload) && payload.some((row: any) => row?.date || row?.count)
          ? payload
          : undefined;

  if (source) {
    return source
        .map((row: any, index: number) => ({
        id: String(row?.id ?? row?.eventDate ?? row?.date ?? row?.logDate ?? index),
        date: getDateFromValue(row?.eventDate ?? row?.date ?? row?.logDate ?? row?.day ?? row?.pk ?? ""),
        count: toNumber(row?.count ?? row?.total ?? row?.value ?? row?.cnt),
      }))
      .sort((a: DailyCountRow, b: DailyCountRow) => a.date.localeCompare(b.date));
  }

  const countsByDate = logs.reduce<Record<string, number>>((acc, row) => {
    const date = getLogDate(row);
    if (!date) return acc;
    acc[date] = (acc[date] ?? 0) + 1;
    return acc;
  }, {});

  return Object.entries(countsByDate)
    .map(([date, count]) => ({ id: date, date, count }))
    .sort((a, b) => a.date.localeCompare(b.date));
}

const AdminStatsDetail = (props: PropsType) => {
  const navigate = useNavigate();
  const params = useParams();
  const [searchParams] = useSearchParams();
  const defaultRange = useMemo(getDefaultDateRange, []);
  const initialRange = useMemo(
    () => ({
      startDate: searchParams.get("startDate") ?? defaultRange.startDate,
      endDate: searchParams.get("endDate") ?? defaultRange.endDate,
    }),
    [defaultRange.endDate, defaultRange.startDate, searchParams],
  );
  const [startDate, setStartDate] = useState(initialRange.startDate);
  const [endDate, setEndDate] = useState(initialRange.endDate);
  const [dailyCounts, setDailyCounts] = useState<DailyCountRow[]>([]);
  const [rawLogs, setRawLogs] = useState<RawLogRow[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const metric = METRICS.find((item) => item.type === params.metricType);

  const fetchDetail = useCallback(async (queryStartDate: string, queryEndDate: string) => {
    if (!metric) return;

    setIsLoading(true);
    setErrorMessage("");
    try {
      const query = {
        eventType: metric.type,
        fromDate: queryStartDate,
        toDate: queryEndDate,
        userId: props.userState.userId,
      };
      const [dailyRes, detailRes] = await Promise.all([
        mainApi.get_dashboard_daily_stats(query),
        mainApi.get_dashboard_event_detail(query),
      ]);

      if (dailyRes.code === "200" && detailRes.code === "200") {
        const dailyPayload = getResultPayload(dailyRes);
        const detailPayload = getResultPayload(detailRes);
        const nextRawLogs = normalizeRawLogs(detailPayload);
        setRawLogs(nextRawLogs);
        setDailyCounts(normalizeDailyCounts(dailyPayload, nextRawLogs));
      } else {
        setRawLogs([]);
        setDailyCounts([]);
        setErrorMessage(dailyRes.response?.error_msg ?? detailRes.response?.error_msg ?? "상세 지표를 조회하지 못했습니다.");
      }
    } catch (error) {
      setRawLogs([]);
      setDailyCounts([]);
      setErrorMessage("상세 지표를 조회하지 못했습니다.");
    } finally {
      setIsLoading(false);
    }
  }, [metric, props.userState.userId]);

  useEffect(() => {
    fetchDetail(initialRange.startDate, initialRange.endDate);
  }, [fetchDetail, initialRange.endDate, initialRange.startDate]);

  if (!metric) return <Navigate replace to="/adminstats" />;

  const chartOptions: Highcharts.Options = {
    chart: {
      type: "column",
    },
    title: {
      text: `${metric.label} 일별 count`,
      align: "left",
    },
    xAxis: {
      categories: dailyCounts.map((row) => row.date),
      crosshair: true,
    },
    yAxis: {
      min: 0,
      allowDecimals: false,
      title: {
        text: "count",
      },
    },
    tooltip: {
      shared: true,
    },
    legend: {
      enabled: false,
    },
    series: [
      {
        name: metric.label,
        type: "column",
        data: dailyCounts.map((row) => row.count),
      },
    ],
  };

  const logColumns: GridColDef[] = [
    { field: "displayTime", headerName: "발생일시", width: 180 },
    { field: "eventType", headerName: "이벤트 타입", width: 230 },
    { field: "userId", headerName: "사용자 ID", width: 140 },
    { field: "userName", headerName: "사용자명", width: 130 },
    { field: "path", headerName: "경로", width: 220 },
    { field: "source", headerName: "소스", width: 130 },
    {
      field: "raw",
      headerName: "원본",
      flex: 1,
      minWidth: 240,
      valueGetter: (params) => JSON.stringify(params.row.raw),
    },
  ];

  const dailyColumns: GridColDef[] = [
    { field: "date", headerName: "일자", flex: 1, minWidth: 130 },
    { field: "count", headerName: "count", flex: 1, minWidth: 100 },
  ];

  return (
    <Stack
      spacing={3}
      sx={{
        minHeight: "100%",
        boxSizing: "border-box",
        p: { xs: 2, md: 3 },
        backgroundColor: "#f6f7fb",
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Button
          variant="outlined"
          size="small"
          startIcon={<ArrowBackIosNewIcon />}
          onClick={() => navigate("/adminstats")}
          sx={{ borderRadius: "4px", backgroundColor: "#fff" }}
        >
          목록
        </Button>
        <Box>
          <Typography variant="h5" fontWeight={800} color="#202633">
            {metric.label}
          </Typography>
          <Typography variant="body2" color="#667085" sx={{ mt: 0.5 }}>
            {metric.description}
          </Typography>
        </Box>
      </Stack>

      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 1.5,
          alignItems: "center",
          p: 2,
          border: "1px solid #dfe4ec",
          borderRadius: "8px",
          backgroundColor: "#fff",
        }}
      >
        <TextField
          label="시작일"
          type="date"
          size="small"
          value={startDate}
          onChange={(event) => setStartDate(event.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          label="종료일"
          type="date"
          size="small"
          value={endDate}
          onChange={(event) => setEndDate(event.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <Button
          variant="contained"
          onClick={() => fetchDetail(startDate, endDate)}
          sx={{ height: 40, borderRadius: "4px", boxShadow: "none" }}
        >
          조회
        </Button>
      </Box>

      {isLoading && <LinearProgress />}
      {errorMessage && <Alert severity="warning">{errorMessage}</Alert>}

      <Box
        sx={{
          p: 2,
          border: "1px solid #dfe4ec",
          borderRadius: "8px",
          backgroundColor: "#fff",
        }}
      >
        <HighchartsReact highcharts={Highcharts} options={chartOptions} />
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <Box
            sx={{
              p: 2,
              border: "1px solid #dfe4ec",
              borderRadius: "8px",
              backgroundColor: "#fff",
            }}
          >
            <Typography variant="subtitle1" fontWeight={800} sx={{ mb: 1.5 }}>
              일별 count
            </Typography>
            <DataGrid
              rows={dailyCounts}
              columns={dailyColumns}
              disableRowSelectionOnClick
              style={{ height: 360 }}
              getRowId={(row) => row.id}
            />
          </Box>
        </Grid>
        <Grid item xs={12} md={8}>
          <Box
            sx={{
              p: 2,
              border: "1px solid #dfe4ec",
              borderRadius: "8px",
              backgroundColor: "#fff",
            }}
          >
            <Typography variant="subtitle1" fontWeight={800} sx={{ mb: 1.5 }}>
              raw 로그 목록
            </Typography>
            <DataGrid
              rows={rawLogs}
              columns={logColumns}
              disableRowSelectionOnClick
              style={{ height: 360 }}
              getRowId={(row) => row.id}
            />
          </Box>
        </Grid>
      </Grid>
    </Stack>
  );
};

export default AdminStatsDetail;
